import { CalendarIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

function parseIsoDate(value) {
  if (!value || typeof value !== "string") return undefined;
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

function toIsoDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function DatePicker({
  value,
  onChange,
  className,
  placeholder = "dd-mm-yyyy",
  disabled,
  testId,
  fromYear: fromYearProp,
  toYear: toYearProp,
  ...calendarProps
}) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => parseIsoDate(value), [value]);
  const yearBounds = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return {
      fromYear: fromYearProp ?? 1970,
      toYear: toYearProp ?? currentYear + 15,
    };
  }, [fromYearProp, toYearProp]);
  const [viewMonth, setViewMonth] = useState(selected || new Date());

  useEffect(() => {
    if (open) {
      setViewMonth(selected || new Date());
    }
  }, [open, value]);

  const years = useMemo(() => {
    const list = [];
    for (let y = yearBounds.fromYear; y <= yearBounds.toYear; y += 1) {
      list.push(y);
    }
    return list;
  }, [yearBounds.fromYear, yearBounds.toYear]);

  const label = selected
    ? selected.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "justify-between h-9 px-3 font-normal text-left border-slate-200 bg-white shadow-sm transition-colors hover:border-[var(--erp-primary)]/40 hover:bg-[var(--erp-primary)]/[0.04] focus-visible:ring-[var(--erp-ring)]",
            !selected && "text-slate-400",
            selected && "text-slate-700",
            className,
          )}
        >
          <span className="truncate">{label}</span>
          <CalendarIcon
            className={cn(
              "w-3.5 h-3.5 shrink-0",
              selected ? "text-[var(--erp-primary)]" : "opacity-60",
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[320px] p-0 rounded-2xl border border-slate-200 shadow-xl overflow-hidden"
      >
        <div
          className="h-1 w-full"
          style={{ backgroundImage: "var(--erp-gradient)" }}
        />
        <div className="p-3">
          <div className="flex items-center gap-2 mb-3 pb-2.5 border-b border-slate-100">
            <select
              value={viewMonth.getMonth()}
              onChange={(e) => {
                const month = Number(e.target.value);
                setViewMonth(
                  (prev) => new Date(prev.getFullYear(), month, 1),
                );
              }}
              className="h-8 flex-1 rounded-lg border border-slate-200 bg-slate-50 px-2 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-[var(--erp-ring)]/40 focus:border-[var(--erp-primary)]/60"
            >
              {MONTHS.map((month, i) => (
                <option key={month} value={i}>
                  {month}
                </option>
              ))}
            </select>
            <select
              value={viewMonth.getFullYear()}
              onChange={(e) => {
                const year = Number(e.target.value);
                setViewMonth((prev) => new Date(year, prev.getMonth(), 1));
              }}
              className="h-8 w-24 rounded-lg border border-slate-200 bg-slate-50 px-2 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-[var(--erp-ring)]/40 focus:border-[var(--erp-primary)]/60"
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-2.5 text-xs font-medium whitespace-nowrap border-[var(--erp-primary)]/30 text-[var(--erp-primary)] hover:bg-[var(--erp-primary)]/10 hover:text-[var(--erp-primary)]"
              onClick={() => {
                const today = new Date();
                setViewMonth(today);
                onChange?.(toIsoDate(today));
                setOpen(false);
              }}
            >
              Today
            </Button>
          </div>

          <Calendar
            mode="single"
            month={viewMonth}
            onMonthChange={setViewMonth}
            selected={selected}
            onSelect={(d) => {
              onChange?.(toIsoDate(d));
              if (d) setOpen(false);
            }}
            classNames={{
              caption: "hidden",
              nav: "hidden",
            }}
            initialFocus
            {...calendarProps}
          />
          <div className="flex items-center justify-between gap-2 pt-2.5 mt-2 border-t border-slate-100">
            <span className="text-xs text-slate-500">Select a date</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2.5 text-xs text-slate-500 hover:text-red-600 hover:bg-red-50"
              onClick={() => onChange?.("")}
            >
              Clear
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
